import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useBrand } from '@/branding/useBrand'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { MicrosoftLogo, ChevronRight, LockIcon } from './LoginIcons'

const ACCENT = '#6db8b2'       // teal de marca: reglas y separadores decorativos
const ACCENT_TEXT = '#3f7d78'  // mismo teal oscurecido: el claro se queda en 2.3:1 sobre blanco
const SURFACE = '#f0eee9'      // fondo del panel derecho, el mismo de la app
const INK = '#3b4559'          // azul pizarra de marca para titulares

export default function LoginDefault() {
  const { login, isLoading } = useAuth()
  const brand = useBrand()
  const reducedMotion = useReducedMotion()
  const { t } = useTranslation()

  const headline = [
    t('login.airvalHeadline1'),
    t('login.airvalHeadline2'),
    t('login.airvalHeadline3'),
  ]

  return (
    <div className="flex h-[100dvh] flex-col md:h-auto md:min-h-screen md:flex-row" style={{ backgroundColor: SURFACE }}>
      {/* Panel izquierdo — vídeo de fábrica con overlay azul industrial */}
      <div className="relative h-[38dvh] min-h-[180px] md:h-auto md:flex-none md:w-[55%] overflow-hidden flex items-center px-10 md:px-16 md:py-0 text-white">
        {/* fondo — vídeo en bucle; foto estática si el usuario pide menos movimiento */}
        {reducedMotion ? (
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(/avp_login.jpg)' }}
          />
        ) : (
          <video
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover"
            poster="/avp_login.jpg"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          >
            {/* webm primero: el navegador coge el primero que sabe reproducir */}
            <source src="/airval_login.webm" type="video/webm" />
            <source src="/airval_login.mp4" type="video/mp4" />
          </video>
        )}
        {/* overlay azul, suave: deja respirar la foto y sostiene el titular en blanco */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{ background: 'linear-gradient(120deg, rgba(27,42,74,0.68) 0%, rgba(27,42,74,0.46) 45%, rgba(47,92,158,0.28) 100%)' }}
        />
        <div className="relative z-10 max-w-md">
          <h1 className="text-3xl md:text-5xl font-semibold leading-tight tracking-tight [text-shadow:0_1px_12px_rgba(0,0,0,0.4)]">
            {headline.map((line, i) => (
              <span key={line} className="brand-title-in block" style={{ animationDelay: `${i * 140}ms` }}>
                {line}
              </span>
            ))}
          </h1>
          <p className="brand-title-in mt-5 hidden text-sm text-white/85 md:block" style={{ animationDelay: '460ms' }}>
            {t('login.airvalSubline')}
          </p>
          <div
            aria-hidden
            className="brand-title-in mt-4 h-[2px] w-[72px] md:mt-6 md:w-[92px]"
            style={{ backgroundColor: ACCENT, animationDelay: '560ms' }}
          />
        </div>
      </div>

      {/* Panel derecho — tarjeta de acceso */}
      <div className="md:w-[45%] flex-1 md:flex-none flex items-center justify-center px-6 py-6 md:px-10 md:py-16">
        <div className="relative w-full max-w-[580px]">
          {/* pico tipo bocadillo: apunta a la foto, sobra cuando los paneles se apilan */}
          <div
            aria-hidden
            className="hidden md:block absolute left-0 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[3px] bg-white"
          />
          <div className="relative rounded-2xl bg-white px-8 py-8 md:px-14 md:py-12 shadow-[0_24px_60px_-24px_rgba(27,42,74,0.3)]">
            <img
              src={brand.logoLogin}
              alt={brand.name}
              className="brand-title-in mx-auto w-full max-w-[130px] md:max-w-[180px]"
              style={{ animationDelay: '320ms' }}
            />

            <h2
              className="brand-title-in mt-7 text-center text-[28px] font-semibold leading-none tracking-tight md:mt-10 md:text-[40px]"
              style={{ color: INK, animationDelay: '400ms' }}
            >
              {t('login.title')}
            </h2>

            <div
              aria-hidden
              className="brand-title-in mx-auto mt-4 h-[2px] w-[72px] md:mt-5"
              style={{ backgroundColor: ACCENT, animationDelay: '460ms' }}
            />

            <p
              className="brand-title-in mx-auto mt-4 max-w-[380px] text-center text-[14px] leading-relaxed text-brand-muted md:mt-6 md:text-[15px]"
              style={{ animationDelay: '520ms' }}
            >
              {t(brand.loginSubtitleKey ?? 'login.subtitle')}
            </p>

            <button
              onClick={login}
              disabled={isLoading}
              className="brand-title-in mt-6 flex w-full items-center justify-center gap-3 rounded-lg bg-[#2f5c9e] py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-[#254b82] md:mt-9 md:py-4 disabled:opacity-50"
              style={{ animationDelay: '600ms' }}
            >
              <MicrosoftLogo />
              <span>{isLoading ? t('login.redirecting') : t('login.signInWithMicrosoft')}</span>
              <ChevronRight />
            </button>

            <div
              className="brand-title-in mt-4 flex items-center justify-center gap-1.5 text-[11px] md:mt-5 md:text-[12px]"
              style={{ color: ACCENT_TEXT, animationDelay: '680ms' }}
            >
              <LockIcon />
              <span>{t('login.authorizedOnly')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
